/**
 * 视频URL全局缓存管理器
 * 避免在首页和其他页面重复生成相同的视频URL和缩略图URL
 */

import { getOptimizedVideoUrl, generateVideoThumbnailUrl } from './video-optimization';

// 全局缓存存储
const videoUrlCache = new Map();
const thumbnailUrlCache = new Map();
const firstFrameUrlCache = new Map();

// 缓存配置
const CACHE_CONFIG = {
  maxSize: 200, // 最大缓存条目数
  baseTTL: 30 * 60 * 1000, // 30分钟基础缓存时间
  
  // 根据文件大小动态调整缓存时间
  sizeBasedTTL: {
    small: {
      maxSize: 500 * 1024, // 500KB以下
      ttl: 15 * 60 * 1000, // 15分钟
    },
    medium: {
      maxSize: 2 * 1024 * 1024, // 2MB以下
      ttl: 60 * 60 * 1000, // 1小时
    },
    large: {
      maxSize: 10 * 1024 * 1024, // 10MB以下
      ttl: 4 * 60 * 60 * 1000, // 4小时
    },
    xlarge: {
      maxSize: Infinity, // 10MB以上
      ttl: 48 * 60 * 60 * 1000, // 12小时
    }
  },
  
  // 预览图缓存配置
  thumbnail: {
    ttl: 24 * 60 * 60 * 1000, // 24小时
    maxSize: 300
  },
  
  // 首帧缓存配置
  firstFrame: {
    ttl: 48 * 60 * 60 * 1000, // 48小时
    maxSize: 100
  }
};

/**
 * 根据文件大小获取缓存时间
 */
function getCacheTTL(fileSize = 0, type = 'video') {
  if (type === 'thumbnail') {
    return CACHE_CONFIG.thumbnail.ttl;
  }
  
  if (type === 'firstFrame') {
    return CACHE_CONFIG.firstFrame.ttl;
  }
  
  // 根据文件大小确定缓存时间
  const sizeConfig = Object.values(CACHE_CONFIG.sizeBasedTTL).find(config => 
    fileSize <= config.maxSize
  );
  
  return sizeConfig ? sizeConfig.ttl : CACHE_CONFIG.baseTTL;
}

/**
 * 估算文件大小（基于URL特征）
 */
function estimateFileSize(videoUrl) {
  if (!videoUrl || !videoUrl.includes('cdn.wan27ai.co')) {
    return 0;
  }
  
  // 根据视频ID特征估算大小
  const videoId = videoUrl.split('/').pop()?.split('.')[0];
  
  if (!videoId) return 0;
  
  // 根据文件大小数据建立映射关系
  const sizeMap = {
    '3a27a294-e1ff-40fb-98e3-8e4eeb0e3f76': 2.1 * 1024 * 1024, // 2,131 KiB
    'b7c68598-0bad-46fa-8cb5-4e72113f7162': 1.8 * 1024 * 1024, // 1,778 KiB
    '77a13d38-9198-415b-a253-47f9ce4f8c60': 1.3 * 1024 * 1024, // 1,314 KiB
    '33b57d9a-94d4-41b7-9105-2ebac3040b0d': 1.1 * 1024 * 1024, // 1,106 KiB
  };
  
  return sizeMap[videoId] || 1.5 * 1024 * 1024; // 默认1.5MB
}

/**
 * 清理过期缓存
 */
function cleanupExpiredCache() {
  const now = Date.now();
  
  // 清理视频URL缓存
  for (const [key, value] of videoUrlCache.entries()) {
    const ttl = getCacheTTL(value.estimatedSize, 'video');
    if (now - value.timestamp > ttl) {
      videoUrlCache.delete(key);
    }
  }
  
  // 清理缩略图缓存
  for (const [key, value] of thumbnailUrlCache.entries()) {
    if (now - value.timestamp > CACHE_CONFIG.thumbnail.ttl) {
      thumbnailUrlCache.delete(key);
    }
  }
  
  // 清理首帧缓存
  for (const [key, value] of firstFrameUrlCache.entries()) {
    if (now - value.timestamp > CACHE_CONFIG.firstFrame.ttl) {
      firstFrameUrlCache.delete(key);
    }
  }
}

/**
 * 限制缓存大小
 */
function limitCacheSize() {
  // 限制视频URL缓存大小
  if (videoUrlCache.size > CACHE_CONFIG.maxSize) {
    const entries = Array.from(videoUrlCache.entries());
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
    
    const toDelete = entries.slice(0, videoUrlCache.size - CACHE_CONFIG.maxSize);
    toDelete.forEach(([key]) => videoUrlCache.delete(key));
  }
  
  // 限制缩略图缓存大小
  if (thumbnailUrlCache.size > CACHE_CONFIG.thumbnail.maxSize) {
    const entries = Array.from(thumbnailUrlCache.entries());
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
    
    const toDelete = entries.slice(0, thumbnailUrlCache.size - CACHE_CONFIG.thumbnail.maxSize);
    toDelete.forEach(([key]) => thumbnailUrlCache.delete(key));
  }
  
  // 限制首帧缓存大小
  if (firstFrameUrlCache.size > CACHE_CONFIG.firstFrame.maxSize) {
    const entries = Array.from(firstFrameUrlCache.entries());
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
    
    const toDelete = entries.slice(0, firstFrameUrlCache.size - CACHE_CONFIG.firstFrame.maxSize);
    toDelete.forEach(([key]) => firstFrameUrlCache.delete(key));
  }
}

/**
 * 获取缓存的视频URL
 * @param {string} videoUrl 原始视频URL
 * @param {string} scenario 优化场景
 * @param {string} deviceType 设备类型
 * @returns {string} 优化后的视频URL
 */
export function getCachedVideoUrl(videoUrl, scenario = 'card', deviceType = 'auto') {
  if (!videoUrl) return '';
  
  const cacheKey = `${videoUrl}_${scenario}_${deviceType}`;
  const estimatedSize = estimateFileSize(videoUrl);
  
  // 检查缓存
  if (videoUrlCache.has(cacheKey)) {
    const cached = videoUrlCache.get(cacheKey);
    const ttl = getCacheTTL(estimatedSize, 'video');
    
    if (Date.now() - cached.timestamp < ttl) {
      return cached.url;
    } else {
      videoUrlCache.delete(cacheKey);
    }
  }
  
  // 生成新的优化URL
  const optimizedUrl = getOptimizedVideoUrl(videoUrl, scenario, deviceType);
  
  // 缓存结果
  videoUrlCache.set(cacheKey, {
    url: optimizedUrl,
    timestamp: Date.now(),
    estimatedSize: estimatedSize
  });
  
  console.log('🎬 Generated and cached video URL:', cacheKey, `(Size: ${Math.round(estimatedSize/1024/1024*100)/100}MB, TTL: ${Math.round(getCacheTTL(estimatedSize, 'video')/1000/60)}min)`);
  
  // 清理缓存
  cleanupExpiredCache();
  limitCacheSize();
  
  return optimizedUrl;
}

/**
 * 获取缓存的缩略图URL
 * @param {string} videoUrl 原始视频URL
 * @param {number} width 图片宽度
 * @param {number} height 图片高度
 * @param {number} quality 图片质量
 * @param {string} time 提取时间
 * @returns {string} 缩略图URL
 */
export function getCachedThumbnailUrl(
  videoUrl, 
  width = 480, 
  height = 360, 
  quality = 98, 
  time = '3s'
) {
  if (!videoUrl) return '';
  
  const cacheKey = `${videoUrl}_${width}x${height}_q${quality}_t${time}`;
  
  // 检查缓存
  if (thumbnailUrlCache.has(cacheKey)) {
    const cached = thumbnailUrlCache.get(cacheKey);
    if (Date.now() - cached.timestamp < CACHE_CONFIG.thumbnail.ttl) {
      console.log('🎬 Using cached thumbnail URL:', cacheKey, `(TTL: ${Math.round(CACHE_CONFIG.thumbnail.ttl/1000/60/60)}h)`);
      return cached.url;
    } else {
      thumbnailUrlCache.delete(cacheKey);
    }
  }
  
  // 生成新的缩略图URL
  const thumbnailUrl = generateVideoThumbnailUrl(videoUrl, width, height, quality, time, 'jpg', 'cover');
  
  // 缓存结果
  thumbnailUrlCache.set(cacheKey, {
    url: thumbnailUrl,
    timestamp: Date.now()
  });
  
  console.log('🎬 Generated and cached thumbnail URL:', cacheKey, `(TTL: ${Math.round(CACHE_CONFIG.thumbnail.ttl/1000/60/60)}h)`);
  
  // 清理缓存
  cleanupExpiredCache();
  limitCacheSize();
  
  return thumbnailUrl;
}

/**
 * 获取缓存的首帧URL
 * @param {string} videoUrl 原始视频URL
 * @returns {string} 首帧URL
 */
export function getCachedFirstFrameUrl(videoUrl) {
  if (!videoUrl) return '';
  
  // 检查缓存
  if (firstFrameUrlCache.has(videoUrl)) {
    const cached = firstFrameUrlCache.get(videoUrl);
    if (Date.now() - cached.timestamp < CACHE_CONFIG.firstFrame.ttl) {
      console.log('🎬 Using cached first frame URL:', videoUrl, `(TTL: ${Math.round(CACHE_CONFIG.firstFrame.ttl/1000/60/60)}h)`);
      return cached.url;
    } else {
      firstFrameUrlCache.delete(videoUrl);
    }
  }
  
  // 生成新的首帧URL - 使用默认参数确保一致性
  const firstFrameUrl = generateVideoThumbnailUrl(videoUrl, 386, 290, 98, '3s', 'jpg', 'cover');
  
  // 缓存结果
  firstFrameUrlCache.set(videoUrl, {
    url: firstFrameUrl,
    timestamp: Date.now()
  });
  
  console.log('🎬 Generated and cached first frame URL:', videoUrl, `(TTL: ${Math.round(CACHE_CONFIG.firstFrame.ttl/1000/60/60)}h)`);
  
  // 清理缓存
  cleanupExpiredCache();
  limitCacheSize();
  
  return firstFrameUrl;
}

// 定期清理过期缓存
if (typeof window !== 'undefined') {
  // 每5分钟清理一次过期缓存
  setInterval(() => {
    cleanupExpiredCache();
  }, 5 * 60 * 1000);
} 